import { useFlags } from "launchdarkly-react-client-sdk";
import { Suspense } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import ErrorBoundary from "components/error-boundary";
import { AMBIENT_SCRIBE_PROCESSING_EVENTS } from "constants/feature-flags";
import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";
import ProcessingEventsTimeline from "features/medical-scribe/medical-scribes-section/scribes-list/scribe-details-dialog/view-scribe-details/tabs/processing-events-timeline/processing-events-timeline.component";
import type { AmbientScribeNote } from "services/types/ambient-scribe-note.type";
import type { AmbientScribeTemplateType } from "services/types/ambient-scribe-template.type";
import type { AmbientScribe } from "services/types/ambient-scribe.type";
import { lazyWithRetry } from "utils/lazy-with-retry.util";

import AfterVisitSummaryTab from "./tabs/after-visit-summary-tab.component";
import AudiologySummaryTab from "./tabs/audiology-summary-tab.component";
import DebriefTab from "./tabs/debrief-tab.component";
import TextTab from "./tabs/text-tab.component";
import TranscriptTab from "./tabs/transcript-tab.component";

/**
 * `lazyWithRetry` with the reload rung DROPPED (`reloadFallback: false`, ENG-1868). This was a
 * bare `React.lazy` because the helper recovered by reloading, and the Ambient Scribe's own
 * `beforeunload` handler vetoes that navigation while a recording runs — and `React.lazy`
 * memoises its failure on the component object, so a vetoed reload left this tab permanently
 * broken. Without the reload rung the retry is a cache-busted re-import that never navigates, so
 * a transient chunk failure recovers in place and a real one still lands in the ErrorBoundary
 * below. `features/sales-coach/` is also a lazily-loaded route chunk that this modal must not
 * drag in for every user.
 */
const SalesCoachReviewTab = lazyWithRetry(
  () =>
    import("features/sales-coach/embedded/sales-coach-review-tab.component"),
  "sales-coach-review-tab",
  { reloadFallback: false }
);

const SALES_COACH_LOAD_FAILED = (
  <Box sx={{ p: 3 }}>
    <Typography variant="body3" color="text.secondary">
      The Sales Coach review couldn&rsquo;t be loaded.
    </Typography>
  </Box>
);

const PANEL_SX = { flex: 1, minHeight: 0, overflow: "auto" } as const;

export type ScribeGenerationProps = {
  isGeneratingSoap: boolean;
  isGeneratingSummary: boolean;
  isGeneratingTranscript: boolean;
  isGeneratingAudiologySummary: boolean;
  isGeneratingAfterVisitSummary: boolean;
  isGeneratingDebrief: boolean;
  debriefTimedOut: boolean;
  onGenerateSoap: () => void;
  onGenerateSummary: () => void;
  onGenerateTranscript: () => void;
  /** GenerateScribeSection fired its own request; holds the SOAP latch without a poll's wait. */
  onSoapGenerateStarted: () => void;
  onAudiologySummaryGenerateStarted: () => void;
  onAfterVisitSummaryGenerateStarted: () => void;
  onDebriefGenerateStarted: () => void;
};

type ScribeDetailPanelProps = {
  remote: AmbientScribe;
  tabView: string;
  /** Loading or refetching: the note tabs hold their content back meanwhile. */
  isLoading: boolean;
  duration: string | undefined;
  generation: ScribeGenerationProps;
  isAudiologyMode: boolean;
  audiologyReportId: number | undefined;
  templateType: AmbientScribeTemplateType | undefined;
  soapPrompt: string | undefined;
  soapFailure: AmbientScribeNote | undefined;
  salesCoachReviewId: number | null;
};

type NotePanelProps = Omit<ScribeDetailPanelProps, "salesCoachReviewId">;

/** Summary, Scribe and the letter: the tabs written from the transcript. */
function ScribeNotePanel({
  remote,
  tabView,
  isLoading,
  generation,
  isAudiologyMode,
  audiologyReportId,
  templateType,
  soapPrompt,
  soapFailure
}: NotePanelProps) {
  const scribeId = Number(remote.id);

  if (tabView === "summary") {
    return (
      <TextTab
        id={scribeId}
        value={String(remote.summary ?? "")}
        type="summary"
        isLoading={isLoading}
        isGenerating={generation.isGeneratingSummary}
        onGenerate={generation.onGenerateSummary}
      />
    );
  }
  if (tabView === "avs") {
    return (
      <AfterVisitSummaryTab
        scribeId={scribeId}
        patientId={remote.patient?.id}
        isGenerating={generation.isGeneratingAfterVisitSummary}
        onGenerateStarted={generation.onAfterVisitSummaryGenerateStarted}
      />
    );
  }
  if (isAudiologyMode) {
    return (
      <AudiologySummaryTab
        scribeId={scribeId}
        reportId={audiologyReportId!}
        isGenerating={generation.isGeneratingAudiologySummary}
        onGenerateStarted={generation.onAudiologySummaryGenerateStarted}
      />
    );
  }
  return (
    <TextTab
      id={scribeId}
      value={String(remote.soap_notes ?? "")}
      type="soap"
      isLoading={isLoading}
      isGenerating={generation.isGeneratingSoap}
      onGenerate={generation.onGenerateSoap}
      onGenerateStarted={generation.onSoapGenerateStarted}
      scribeTemplateId={remote.ambient_scribe_template_id}
      templateType={templateType}
      prompt={soapPrompt}
      soapFailure={soapFailure}
    />
  );
}

const NOTE_TABS = new Set(["summary", "soap", "avs"]);

/** The open detail tab's content. (Scribe Context is mounted on its own, hidden while closed.) */
export default function ScribeDetailPanel(props: ScribeDetailPanelProps) {
  const { remote, tabView, duration, generation, salesCoachReviewId } = props;
  const flags = useFlags();
  const theme = useTheme();
  const scribeId = Number(remote.id);

  if (NOTE_TABS.has(tabView)) {
    // The note tabs need a transcript to read from.
    if (!remote.has_transcription || !remote.id) return null;
    return (
      <Box sx={PANEL_SX}>
        <ScribeNotePanel {...props} />
      </Box>
    );
  }

  switch (tabView) {
    case "transcript":
      return (
        <Box sx={PANEL_SX}>
          <TranscriptTab
            id={scribeId}
            transcription={remote.transcription}
            duration={duration}
            status={remote.status}
            isGeneratingTranscription={generation.isGeneratingTranscript}
            onTranscribe={generation.onGenerateTranscript}
          />
        </Box>
      );
    case "debrief":
      return remote.id ? (
        <Box sx={PANEL_SX}>
          <DebriefTab
            scribeId={scribeId}
            consultDebriefId={remote.consult_debrief_id}
            isGeneratingDebrief={generation.isGeneratingDebrief}
            hasTimedOut={generation.debriefTimedOut}
            onGenerateStarted={generation.onDebriefGenerateStarted}
          />
        </Box>
      ) : null;
    case "salesCoach":
      // `overflow: auto`, like the others — NOT the context panel's `hidden`, which would clip a
      // long review with no scrollbar and no error.
      return (
        <Box sx={PANEL_SX}>
          <ErrorBoundary FallbackComponent={SALES_COACH_LOAD_FAILED}>
            <Suspense fallback={<CircularProgress />}>
              {/* Re-analyse's confirm must paint above this modal (`snackbar + 101`). */}
              <SalesCoachReviewTab
                reviewId={salesCoachReviewId}
                confirmZIndex={getScribeModalNestedDialogZIndex(
                  theme.zIndex.snackbar
                )}
              />
            </Suspense>
          </ErrorBoundary>
        </Box>
      );
    case "events":
      return flags[AMBIENT_SCRIBE_PROCESSING_EVENTS] && remote.id ? (
        <Box sx={PANEL_SX}>
          <ProcessingEventsTimeline key={remote.id} scribeId={remote.id} />
        </Box>
      ) : null;
    default:
      return null;
  }
}
